/**
 * Lista de deseos personalizada sobre Smart Wishlist (Webmarked).
 *
 * - Lee la lista desde el app proxy de la app: /a/wishlist?type=api
 *   · cliente con sesión → customerid (id del cliente de Shopify)
 *   · invitado           → wishlistid (cookie "wishlist_id" que crea la app)
 * - Completa cada producto con /products/{handle}.js (precio, precio de comparación,
 *   tallas y disponibilidad) — sin token de Storefront API.
 * - Agrega al carrito con /cart/add.js y refresca el cart drawer del tema.
 * - Elimina con RemoveFromSmartWishlist() de la propia app, para que quede sincronizado.
 */
(() => {
  'use strict';

  if (window.MajeWishlistLoaded) return;
  window.MajeWishlistLoaded = true;

  const SIZE_OPTION_NAMES = ['talla', 'tallas', 'size', 'taille'];
  const COLOR_OPTION_NAMES = ['color', 'colour', 'couleur'];
  const IMAGE_WIDTHS = [360, 540, 720, 900, 1080];

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);

  const readCookie = (name) => {
    const prefix = `${name}=`;
    const parts = document.cookie ? document.cookie.split(';') : [];
    for (const rawPart of parts) {
      const part = rawPart.trim();
      if (part.indexOf(prefix) !== 0) continue;
      let value = part.substring(prefix.length);
      try {
        value = decodeURIComponent(value);
      } catch (error) {
        /* valor sin codificar */
      }
      return value.replace(/^"+|"+$/g, '');
    }
    return '';
  };

  const waitFor = (test, timeout = 4000, interval = 150) =>
    new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (test()) return resolve(true);
        if (Date.now() - start >= timeout) return resolve(false);
        setTimeout(tick, interval);
      };
      tick();
    });

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN';
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const toCents = (value) => {
    const number = parseFloat(String(value == null ? '' : value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(number) ? Math.round(number * 100) : 0;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    const base = url.indexOf('//') === 0 ? `https:${url}` : url;
    return `${base}${base.indexOf('?') === -1 ? '?' : '&'}width=${width}`;
  };

  const optionIndex = (product, names) => {
    const options = (product && product.options) || [];
    return options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option && option.name;
      return names.indexOf(String(name || '').trim().toLowerCase()) !== -1;
    });
  };

  class MajeWishlist {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-mw-grid]');
      this.statusEl = root.querySelector('[data-mw-status]');
      this.emptyEl = root.querySelector('[data-mw-empty]');
      this.errorEl = root.querySelector('[data-mw-error]');
      this.countEl = root.querySelector('[data-mw-count]');
      this.loginEl = root.querySelector('[data-mw-login]');

      const data = root.dataset;
      this.isLogged = data.logged === 'true';
      this.customerId = data.customerId || '';
      this.columns = parseInt(data.columns, 10) || 4;
      this.cartUrl = data.cartUrl || '/cart';
      this.cartAddUrl = data.cartAddUrl || '/cart/add';
      this.text = {
        add: data.textAdd || 'Agregar al carrito',
        added: data.textAdded || 'Agregado',
        selectSize: data.textSelectSize || 'Selecciona tu talla',
        soldOut: data.textSoldOut || 'Agotado',
        unavailable: data.textUnavailable || 'Ya no está disponible',
        remove: data.textRemove || 'Eliminar de la lista de deseos',
        errorAdd: data.textErrorAdd || 'No se pudo agregar. Inténtalo de nuevo.',
        countOne: data.textCountOne || '1 artículo',
        countOther: data.textCountOther || '[count] artículos',
      };

      this.busy = false;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.grid.addEventListener('click', this.onClick);
      this.root.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', (event) => {
        const open = this.grid.querySelector('[data-mw-toggle][aria-expanded="true"]');
        if (!open) return;
        const actions = open.closest('.maje-wishlist__actions');
        if (actions && !actions.contains(event.target)) this.togglePanel(open, false);
      });

      this.load();
    }

    /* ---------- Datos ---------- */

    getWishlistQuery() {
      if (this.isLogged && this.customerId) return { param: 'customerid', id: this.customerId };
      const id = readCookie('wishlist_id');
      return { param: 'wishlistid', id };
    }

    async fetchWishlist() {
      const { param, id } = this.getWishlistQuery();
      if (!id || id === '0' || id === 'undefined' || id === 'null') return [];

      const url = `/a/wishlist?type=api&${param}=${encodeURIComponent(id)}&version=1&_=${Date.now()}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Wishlist API ${response.status}`);

      const body = await response.text();
      if (!body.trim()) return [];
      const data = JSON.parse(body);
      return Array.isArray(data && data.items) ? data.items : [];
    }

    async fetchProduct(handle) {
      if (!handle) return null;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    dedupe(items) {
      const seen = new Set();
      return items.filter((item) => {
        if (!item || !item.id) return false;
        const key = `${item.id}:${item.variant_id || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    async load() {
      this.setState('loading');
      try {
        const items = this.dedupe(await this.fetchWishlist());
        if (!items.length) {
          this.setState('empty');
          return;
        }
        const products = await Promise.all(items.map((item) => this.fetchProduct(item.handle)));
        this.grid.innerHTML = items.map((item, index) => this.renderItem(item, products[index], index)).join('');
        this.setState('ready');
      } catch (error) {
        console.error('[maje-wishlist] Error al cargar la lista:', error);
        this.setState('error');
      }
    }

    /* ---------- Render ---------- */

    buildCard(item, product) {
      const card = {
        productId: String(item.id),
        variantId: String(item.variant_id || ''),
        handle: item.handle || '',
        title: item.title || (product && product.title) || '',
        image: item.image || '',
        price: toCents(item.variant_price),
        compareAt: 0,
        color: '',
        sizes: [],
        directVariant: null,
        available: false,
        missing: !product,
      };

      if (!product) return card;

      const variants = product.variants || [];
      const saved = variants.find((variant) => String(variant.id) === card.variantId) || variants[0];
      if (!saved) return card;

      const sizeIndex = optionIndex(product, SIZE_OPTION_NAMES);
      const colorIndex = optionIndex(product, COLOR_OPTION_NAMES);
      const savedColor = colorIndex >= 0 ? saved.options[colorIndex] : null;

      card.variantId = String(saved.id);
      card.title = product.title || card.title;
      card.price = saved.price;
      card.compareAt = saved.compare_at_price && saved.compare_at_price > saved.price ? saved.compare_at_price : 0;
      card.color = savedColor || '';
      card.image =
        (saved.featured_image && saved.featured_image.src) || product.featured_image || card.image;

      const sameColor = variants.filter(
        (variant) => colorIndex < 0 || variant.options[colorIndex] === savedColor
      );

      if (sameColor.length <= 1) {
        card.directVariant = saved;
        card.available = Boolean(saved.available);
      } else {
        card.sizes = sameColor.map((variant) => ({
          id: String(variant.id),
          label: sizeIndex >= 0 ? variant.options[sizeIndex] : variant.title,
          available: Boolean(variant.available),
        }));
        card.available = card.sizes.some((size) => size.available);
      }

      return card;
    }

    renderImage(card) {
      if (!card.image) return '<span class="maje-wishlist__placeholder" aria-hidden="true"></span>';
      const desktopVw = Math.ceil(100 / this.columns);
      const srcset = IMAGE_WIDTHS.map((width) => `${sizedImage(card.image, width)} ${width}w`).join(', ');
      return `<img
          class="maje-wishlist__image"
          src="${escapeHtml(sizedImage(card.image, 720))}"
          srcset="${escapeHtml(srcset)}"
          sizes="(min-width: 990px) ${desktopVw}vw, (min-width: 750px) 33vw, 50vw"
          alt="${escapeHtml(card.title)}"
          width="720"
          height="960"
          loading="lazy"
        >`;
    }

    renderActions(card, index) {
      if (card.missing) {
        return `<p class="maje-wishlist__note">${escapeHtml(this.text.unavailable)}</p>`;
      }
      if (!card.available) {
        return `<p class="maje-wishlist__note">${escapeHtml(this.text.soldOut)}</p>`;
      }
      if (card.directVariant) {
        return `<button type="button" class="maje-wishlist__add" data-mw-add data-variant-id="${escapeHtml(card.directVariant.id)}">
            <span class="maje-wishlist__add-label">${escapeHtml(this.text.add)}</span>
          </button>
          <p class="maje-wishlist__msg" data-mw-msg role="alert" hidden></p>`;
      }

      const panelId = `${this.root.id}-sizes-${index}`;
      const sizes = card.sizes
        .map(
          (size) => `<button
              type="button"
              class="maje-wishlist__size${size.available ? '' : ' is-sold-out'}"
              data-mw-add
              data-variant-id="${escapeHtml(size.id)}"
              ${size.available ? '' : `disabled aria-label="${escapeHtml(`${size.label} – ${this.text.soldOut}`)}"`}
            >${escapeHtml(size.label)}</button>`
        )
        .join('');

      return `<button type="button" class="maje-wishlist__add" data-mw-toggle aria-expanded="false" aria-controls="${panelId}">
          <span class="maje-wishlist__add-label">${escapeHtml(this.text.add)}</span>
        </button>
        <div class="maje-wishlist__sizes" id="${panelId}" data-mw-sizes hidden>
          <p class="maje-wishlist__sizes-label">${escapeHtml(this.text.selectSize)}</p>
          <div class="maje-wishlist__sizes-list">${sizes}</div>
        </div>
        <p class="maje-wishlist__msg" data-mw-msg role="alert" hidden></p>`;
    }

    renderItem(item, product, index) {
      const card = this.buildCard(item, product);
      const url = card.handle
        ? `/products/${encodeURIComponent(card.handle)}${card.variantId ? `?variant=${card.variantId}` : ''}`
        : '#';
      const discount = card.compareAt ? Math.round((1 - card.price / card.compareAt) * 100) : 0;
      const removeLabel = `${this.text.remove}: ${card.title}`;

      return `<li
          class="maje-wishlist__item${card.missing || !card.available ? ' is-unavailable' : ''}"
          data-mw-item
          data-product-id="${escapeHtml(card.productId)}"
          data-variant-id="${escapeHtml(item.variant_id || card.variantId)}"
        >
          <div class="maje-wishlist__media">
            <a href="${escapeHtml(url)}" class="maje-wishlist__media-link" tabindex="-1" aria-hidden="true">
              ${this.renderImage(card)}
            </a>
            ${discount > 0 ? `<span class="maje-wishlist__badge">-${discount}%</span>` : ''}
            <button type="button" class="maje-wishlist__remove" data-mw-remove aria-label="${escapeHtml(removeLabel)}">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                <path d="M12 20.6l-1.3-1.2C5.8 15 2.6 12.1 2.6 8.5 2.6 5.6 4.9 3.3 7.8 3.3c1.6 0 3.2.8 4.2 2 1-1.2 2.6-2 4.2-2 2.9 0 5.2 2.3 5.2 5.2 0 3.6-3.2 6.5-8.1 10.9L12 20.6z"/>
              </svg>
            </button>
          </div>
          <div class="maje-wishlist__info">
            <a href="${escapeHtml(url)}" class="maje-wishlist__name">${escapeHtml(card.title)}</a>
            ${card.color ? `<p class="maje-wishlist__variant">${escapeHtml(card.color)}</p>` : ''}
            <p class="maje-wishlist__price">
              ${card.compareAt ? `<s class="maje-wishlist__price-compare">${formatMoney(card.compareAt)}</s>` : ''}
              <span class="maje-wishlist__price-final${card.compareAt ? ' is-sale' : ''}">${formatMoney(card.price)}</span>
            </p>
          </div>
          <div class="maje-wishlist__actions">${this.renderActions(card, index)}</div>
        </li>`;
    }

    setState(state) {
      this.statusEl.hidden = state !== 'loading';
      this.emptyEl.hidden = state !== 'empty';
      this.errorEl.hidden = state !== 'error';
      this.grid.hidden = state !== 'ready';
      if (this.loginEl) this.loginEl.hidden = state === 'loading';
      this.root.classList.toggle('is-loading', state === 'loading');
      this.updateCount();
    }

    updateCount() {
      const total = this.grid.hidden ? 0 : this.grid.querySelectorAll('[data-mw-item]').length;
      this.countEl.hidden = total === 0;
      this.countEl.textContent =
        total === 1 ? this.text.countOne : this.text.countOther.replace('[count]', String(total));
    }

    /* ---------- Interacción ---------- */

    onClick(event) {
      const removeButton = event.target.closest('[data-mw-remove]');
      if (removeButton) {
        event.preventDefault();
        this.remove(removeButton.closest('[data-mw-item]'));
        return;
      }

      const toggle = event.target.closest('[data-mw-toggle]');
      if (toggle) {
        event.preventDefault();
        this.togglePanel(toggle);
        return;
      }

      const addButton = event.target.closest('[data-mw-add]');
      if (addButton && !addButton.disabled) {
        event.preventDefault();
        this.addToCart(addButton);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.grid.querySelector('[data-mw-toggle][aria-expanded="true"]');
      if (open) {
        this.togglePanel(open, false);
        open.focus();
      }
    }

    togglePanel(toggle, force) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      const willOpen = typeof force === 'boolean' ? force : toggle.getAttribute('aria-expanded') !== 'true';

      this.grid.querySelectorAll('[data-mw-toggle][aria-expanded="true"]').forEach((other) => {
        if (other === toggle) return;
        other.setAttribute('aria-expanded', 'false');
        const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
        if (otherPanel) otherPanel.hidden = true;
      });

      toggle.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
      if (willOpen) {
        const firstSize = panel.querySelector('[data-mw-add]:not([disabled])');
        if (firstSize) firstSize.focus({ preventScroll: true });
      }
    }

    async remove(itemEl) {
      if (!itemEl) return;
      const ready =
        typeof window.RemoveFromSmartWishlist === 'function' ||
        (await waitFor(() => typeof window.RemoveFromSmartWishlist === 'function', 4000));

      if (!ready) {
        console.error('[maje-wishlist] Smart Wishlist no está disponible para eliminar el producto.');
        return;
      }

      window.RemoveFromSmartWishlist(itemEl.dataset.productId, itemEl.dataset.variantId);
      itemEl.remove();

      if (!this.grid.querySelector('[data-mw-item]')) {
        this.setState('empty');
      } else {
        this.updateCount();
      }
    }

    showMessage(itemEl, message) {
      const messageEl = itemEl && itemEl.querySelector('[data-mw-msg]');
      if (!messageEl) return;
      messageEl.textContent = message;
      messageEl.hidden = !message;
    }

    async addToCart(button) {
      if (this.busy) return;
      const itemEl = button.closest('[data-mw-item]');
      const toggle = itemEl && itemEl.querySelector('[data-mw-toggle]');
      const labelHost = toggle || button;
      const label = labelHost.querySelector('.maje-wishlist__add-label');

      this.busy = true;
      this.showMessage(itemEl, '');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { id: Number(button.dataset.variantId), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${this.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          this.showMessage(itemEl, result.description || result.message || this.text.errorAdd);
          return;
        }

        if (toggle) this.togglePanel(toggle, false);

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'maje-wishlist',
            productVariantId: body.id,
            cartData: result,
          });
        }

        if (cart && typeof cart.renderContents === 'function' && result.sections) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else if (!cart) {
          window.location.href = this.cartUrl;
          return;
        }

        if (label) {
          labelHost.classList.add('is-added');
          label.textContent = this.text.added;
          setTimeout(() => {
            labelHost.classList.remove('is-added');
            label.textContent = this.text.add;
          }, 2000);
        }
      } catch (error) {
        console.error('[maje-wishlist] Error al agregar al carrito:', error);
        this.showMessage(itemEl, this.text.errorAdd);
      } finally {
        this.busy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-maje-wishlist]').forEach((root) => {
      if (root.majeWishlist) return;
      root.majeWishlist = new MajeWishlist(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();